import React, { useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { FaChevronDown, FaChevronRight, FaSyncAlt } from 'react-icons/fa';
import ReactSelect from './ReactSelect';

const BRAND_NAVY   = '#1f3c88';
const BRAND_BORDER = '#dbe8f8';

// Os valores da P.O. Aéreo (`EventoAereoSolicitacao::PO_AEREO`): o select do cabeçalho e o da Nova solicitação.
export const PO_NAO_SE_APLICA = 'NÃO SE APLICA';
export const PO_AEREO = ['', 'EM ANDAMENTO', 'EMITIDO', 'PENDENTE', PO_NAO_SE_APLICA];

const SECOES = [
    {
        titulo: 'Identificação do Cliente',
        campos: [
            { key: 'cliente',      label: 'Cliente', auto: true },
            { key: 'nomeEvento',   label: 'Nome do Evento', auto: true },
            // Produto e BU são a mesma coisa: a área para quem o evento é, que vem dos produtos do
            // orçamento. Um campo só, com todos marcados de padrão; a escolha vai às duas células.
            { key: 'produto',      label: 'Produto / BU', auto: true, tipo: 'multi', espelho: 'bu' },
            { key: 'centroCusto',  label: 'Centro de Custo', auto: true },
            { key: 'ordemInterna', label: 'Ordem Interna', auto: true },
        ],
    },
    {
        titulo: 'Solicitação / Consultor',
        campos: [
            { key: 'solicitante',          label: 'Solicitante', auto: true },
            { key: 'agenciaIntermediaria', label: 'Ag. Intermediária' },
            { key: 'consultor',            label: 'Consultor Access-MKT', auto: true },
            { key: 'emailConsultor',       label: 'E-mail Consultor', auto: true },
            { key: 'telefoneConsultor',    label: 'Telefone Consultor', auto: true },
            // O padrão é o da Nova solicitação do pedido, não o do cadastro do evento.
            { key: 'poAereo',              label: 'P.O. Aéreo', tipo: 'select', auto: true },
        ],
    },
    {
        titulo: 'Valores e Período',
        campos: [
            { key: 'valorPedidoBalao',  label: 'Valor Pedido Balão', tipo: 'moeda' },
            { key: 'periodoEvento',     label: 'Período do Evento', auto: true },
        ],
    },
    {
        titulo: 'Observações (caixa amarela do Modelo Cliente)',
        campos: [
            { key: 'observacoesGerais', label: 'Observações Gerais', tipo: 'textarea', col: 'col-12', rows: 4 },
        ],
    },
];

const labelStyle = { color: '#7b8ba7', fontSize: 12, letterSpacing: '0.06em' };
// Campos auto-preenchidos do cadastro do evento (verde)
const autoStyle = { background: '#eafaf0', borderColor: '#bfe6cd' };
// Keys dos campos verdes — alvo do botão de restaurar padrões do cadastro. O espelho (a BU do
// Produto / BU) volta junto.
const CAMPOS_AUTO = SECOES.flatMap(s => s.campos).filter(c => c.auto).flatMap(c => (c.espelho ? [c.key, c.espelho] : [c.key]));
const TOTAL_CAMPOS = SECOES.flatMap(s => s.campos).length;

// O Produto / BU guardado é a lista de nomes separada por vírgula.
const nomesDaLista = (valor) => String(valor ?? '').split(',').map(s => s.trim()).filter(Boolean);

function Campo({ campo, valor, onChange, disabled, opcoesProduto }) {
    const style = campo.auto ? autoStyle : undefined;
    const comum = {
        className: 'form-control',
        style,
        value: valor ?? '',
        disabled,
        onChange: (e) => onChange(campo.key, e.target.value),
    };

    let input;
    if (campo.tipo === 'multi') {
        // As opções são os produtos do orçamento; o que já estava gravado e não é mais produto do
        // orçamento continua na lista, para não sumir sem aviso.
        const marcados = nomesDaLista(valor);
        const opcoes = [...new Set([...(opcoesProduto ?? []), ...marcados])].map(n => ({ value: n, label: n }));
        input = (
            <ReactSelect isMulti options={opcoes} isDisabled={disabled}
                styles={campo.auto ? { control: (base) => ({ ...base, ...autoStyle }) } : null}
                placeholder={opcoes.length ? 'Escolha' : 'Sem produto no orçamento do evento'}
                value={opcoes.filter(o => marcados.includes(o.value))}
                onChange={(sel) => {
                    const texto = (sel ?? []).map(o => o.value).join(', ');
                    onChange(campo.key, texto);
                    if (campo.espelho) onChange(campo.espelho, texto);
                }} />
        );
    } else if (campo.tipo === 'select') {
        input = (
            <select {...comum}>
                {PO_AEREO.map(op => <option key={op} value={op}>{op || '—'}</option>)}
            </select>
        );
    } else if (campo.tipo === 'textarea') {
        input = <textarea {...comum} rows={campo.rows || 1} style={{ ...style, resize: 'vertical' }} placeholder="Notas livres..." />;
    } else if (campo.tipo === 'moeda') {
        input = (
            <NumericFormat className="form-control" style={style} disabled={disabled}
                value={valor ?? ''} thousandSeparator="." decimalSeparator="," prefix="R$ "
                decimalScale={2} fixedDecimalScale allowNegative={false}
                onValueChange={(v) => onChange(campo.key, v.value)} />
        );
    } else {
        input = <input type="text" {...comum} />;
    }

    return (
        <div className={`${campo.col || 'col-md-4'} mb-3`}>
            <label className="form-label fw-bold text-uppercase mb-1" style={labelStyle}>{campo.label}</label>
            {input}
            {campo.auto && <div style={{ color: '#9fb3d4', fontSize: 11, marginTop: 2 }}>← do cadastro</div>}
        </div>
    );
}

export default function CabecalhoModeloCliente({ dados = {}, padroes = {}, opcoesProduto = [], onChange, disabled = false }) {
    const [aberto, setAberto] = useState(false);

    // Restaura os campos verdes ao valor do cadastro do evento.
    // Não sobrescreve quando o padrão veio vazio (mantém o que está preenchido).
    const restaurarPadroes = (e) => {
        // Spin de uma volta — a Web Animations API recria a animação a cada clique.
        e.currentTarget.querySelector('svg')?.animate(
            [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
            { duration: 600, easing: 'ease' },
        );
        CAMPOS_AUTO.forEach(key => {
            const padrao = padroes?.[key];
            if (padrao != null && String(padrao).trim() !== '') onChange(key, padrao);
        });
    };

    return (
        <div className="bg-white mb-4" style={{ border: `1px solid ${BRAND_BORDER}`, borderRadius: 18, overflow: 'hidden' }}>
            {/* Header clicável */}
            <div className="d-flex align-items-center gap-2 px-4 py-3" style={{ cursor: 'pointer', background: 'linear-gradient(180deg, #fff 0%, #f7fbff 100%)', borderBottom: aberto ? `1px solid ${BRAND_BORDER}` : 'none' }}
                onClick={() => setAberto(a => !a)}>
                {aberto ? <FaChevronDown size={13} style={{ color: BRAND_NAVY }} /> : <FaChevronRight size={13} style={{ color: BRAND_NAVY }} />}
                <span className="fw-bold" style={{ color: BRAND_NAVY, fontSize: 16 }}>Cabeçalho do Modelo Cliente</span>
                <span style={{ color: '#9fb3d4', fontSize: 13 }}>({TOTAL_CAMPOS} campos · verdes vêm do cadastro do evento)</span>
                <span className="ms-auto" style={{ color: '#9fb3d4', fontSize: 13 }}>
                    {aberto ? 'clique para recolher' : 'clique para expandir'}
                </span>
            </div>

            {/* Corpo */}
            {aberto && (
                <div className="px-4 pt-4 pb-2">
                    {!disabled && (
                        <div className="d-flex justify-content-end mb-2">
                            <button type="button" onClick={restaurarPadroes}
                                title="Restaurar campos verdes com os valores do cadastro do evento"
                                className="btn btn-sm d-inline-flex align-items-center justify-content-center border-0 bg-transparent shadow-none p-1"
                                style={{ color: BRAND_NAVY, lineHeight: 1 }}>
                                <FaSyncAlt size={14} />
                            </button>
                        </div>
                    )}
                    {SECOES.map(secao => (
                        <div key={secao.titulo} className="mb-2">
                            <div className="fw-bold text-uppercase mb-2" style={{ color: '#5f6f8a', fontSize: 12, letterSpacing: '0.08em' }}>
                                {secao.titulo}
                            </div>
                            <div className="row">
                                {secao.campos.map(campo => (
                                    <Campo key={campo.key} campo={campo} opcoesProduto={opcoesProduto}
                                        valor={dados[campo.key]} onChange={onChange} disabled={disabled} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
